// OP-3605 — Regras puras da entrada, espelho de `validarCadastroUnidade`
// (domain/cadastro.ts). Gates visuais: a unicidade de verdade é do banco.
import { ItemAtributo, ORIGEM_ENTRADA_LABEL, OrigemEntrada, UnidadePorCodigo, UnidadesPorCodigo } from '../../../../types/estoque'

/** A origem decide o lastro: compra tem a nota fiscal; as demais, o motivo. */
export function impedimentoOrigemEntrada(origem: OrigemEntrada, notaFiscal: string, motivo: string): string | null {
  if (origem === 'COMPRA') return notaFiscal.trim() ? null : 'Informe a nota fiscal: entrada por compra precisa dela como lastro.'
  return motivo.trim() ? null : 'Informe o motivo da entrada.'
}

export function explicacaoOrigem(origem: OrigemEntrada, ehSerial: boolean): string {
  if (origem === 'COMPRA') return 'Compra tem a nota fiscal como lastro; o motivo é opcional.'
  const base = `${ORIGEM_ENTRADA_LABEL[origem]} não precisa de nota fiscal: informe o motivo.`
  if (origem === 'DEVOLUCAO' && ehSerial) {
    return `${base} Peça que já está cadastrada não entra de novo: o retorno dela se registra na conferência da remessa ou da transferência.`
  }
  return base
}

export type CampoDuplicado = 'SERIAL' | 'PATRIMONIO'

export function campoDuplicado(codigo: string): CampoDuplicado | null {
  if (codigo === 'SERIAL_DUPLICADO') return 'SERIAL'
  if (codigo === 'PATRIMONIO_DUPLICADO') return 'PATRIMONIO'
  return null
}

export function pecaJaCadastrada(r: UnidadesPorCodigo | null | undefined, campo: CampoDuplicado): UnidadePorCodigo | null {
  const casadas = (r?.pecas ?? []).filter((p) => p.casouEm === campo)
  return casadas.length === 1 ? casadas[0] : null
}

/**
 * Espelho de `validarCadastroUnidade`, na ordem do domínio.
 *
 * Serial igual ao patrimônio é recusado (comparação sem caixa) porque é o
 * defeito mais comum da base atual: preencher os dois com o mesmo número
 * apaga a diferença entre o que o fabricante gravou e o que a BD etiquetou.
 * Duplicidade é conferida contra a base inteira, e disso só o servidor sabe.
 */
export function impedimentoCadastroPeca(serial: string, patrimonio: string): string | null {
  const s = serial.trim()
  const p = patrimonio.trim()
  if (!s) return 'Informe o serial.'
  if (!p) return 'Informe o patrimônio.'
  if (s.toUpperCase() === p.toUpperCase()) {
    return 'Serial e patrimônio não podem ser o mesmo número: um é do fabricante, o outro é da etiqueta da BD.'
  }
  return null
}

/** A quantidade da entrada: inteiro maior que zero. */
export function impedimentoEntradaQuantidade(quantidade: string): string | null {
  const v = quantidade.trim()
  if (!v) return 'Informe a quantidade.'
  if (!/^\d+$/.test(v) || Number(v) <= 0) return 'A quantidade precisa ser um número inteiro maior que zero.'
  return null
}

/**
 * Os atributos obrigatórios do item precisam vir preenchidos.
 *
 * Quem decide é o servidor (`validarAtributosDaUnidade`, na transação e antes
 * do INSERT). Aqui é gate visual — e ele precisa saber se a DEFINIÇÃO chegou:
 * uma falha ao carregar o detalhe do item entregava uma lista vazia, o que
 * lia como "este item não tem obrigatório nenhum" e liberava o cadastro de
 * uma peça que nasceria incompleta.
 */
export function impedimentoAtributos(
  atributos: ItemAtributo[], valores: Record<string, string>,
  estado?: { carregando?: boolean; erro?: unknown },
): string | null {
  if (estado?.carregando) return 'Carregando os atributos do item…'
  if (estado?.erro) return 'Não foi possível carregar os atributos deste item. Sem eles não dá para cadastrar a peça.'
  const faltando = atributos.filter((a) => Number(a.obrigatorio) === 1 && !String(valores[a.chave] ?? '').trim())
  if (!faltando.length) return null
  return `Preencha ${faltando.length === 1 ? 'o atributo' : 'os atributos'}: ${faltando.map((a) => a.rotulo).join(', ')}.`
}
